/// <reference types="vite/client" />
import type { QueryClient } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useLocation,
} from "@tanstack/react-router";
import * as React from "react";
import { useEffect, useRef } from "react";
import { I18nextProvider } from "react-i18next";
import { getAuthToken, telegramAuthByInitData } from "../services/auth";

declare global {
  interface Window {
    Telegram?: {
      WebApp?: { initData?: string };
    };
  }
}
import { Toaster } from "sonner";
import { TooltipProvider } from "~/components/ui/tooltip";
import { DefaultCatchBoundary } from "../components/DefaultCatchBoundary";
import { NotFound } from "../components/NotFound";
import { LangProvider, useLang } from "../contexts/LangContext";
import i18n from "../i18n/config";
import appCss from "~/styles/app.css?url";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, shrink-to-fit=no",
      },
      {
        httpEquiv: "x-ua-compatible",
        content: "ie=edge",
      },
      {
        httpEquiv: "Content-Type",
        content: "text/html;charset=UTF-8",
      },
      {
        name: "msapplication-TileColor",
        content: "#ffffff",
      },
      {
        name: "theme-color",
        content: "#ffffff",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png",
      },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "icon", href: "/favicon.ico" },
      { rel: "mask-icon", href: "/safari-pinned-tab.svg", color: "#5bbad5" },
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "preconnect", href: "https://api.lotin.uz" },
      { rel: "dns-prefetch", href: "https://api.lotin.uz" },
    ],
    scripts: [
      {
        // GTM (GA4, Yandex Metrica, ...) starts after the page has loaded and
        // the main thread is idle, so its ~400 KB of tags don't compete with
        // the LCP image or the first interactions. dataLayer pushes made
        // earlier are queued and processed once it loads.
        children: `
          (function(w, d, s, l, i) {
            w[l] = w[l] || [];
            function loadGtm() {
              w[l].push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js'
              });
              var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != 'dataLayer' ? '&l=' + l : '';
              j.async = true;
              j.src =
                'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
              f.parentNode.insertBefore(j, f);
            }
            function whenIdle() {
              if (w.requestIdleCallback) {
                w.requestIdleCallback(loadGtm, { timeout: 3000 });
              } else {
                setTimeout(loadGtm, 1500);
              }
            }
            if (d.readyState === 'complete') {
              whenIdle();
            } else {
              w.addEventListener('load', whenIdle, { once: true });
            }
          })(window, document, 'script', 'dataLayer', 'GTM-WW54V5R');
        `,
      },
    ],
  }),
  errorComponent: (props) => {
    return (
      <RootDocument>
        <DefaultCatchBoundary {...props} />
      </RootDocument>
    );
  },
  notFoundComponent: () => <NotFound />,
  component: RootComponent,
});

/**
 * When opened as Telegram Mini App: if initData is present and user has no token,
 * authenticate via init_data and dispatch lotin:auth so existing React auth works.
 */
function MiniAppAuthEffect() {
  const done = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || done.current) return;
    const initData = window.Telegram?.WebApp?.initData;
    if (!initData || getAuthToken()) return;
    done.current = true;
    telegramAuthByInitData(initData).then((result) => {
      if (result.status === "success") {
        window.dispatchEvent(new Event("lotin:auth"));
      }
    });
  }, []);

  return null;
}

function RootComponent() {
  const location = useLocation();
  const firstSegment = location.pathname.split("/").filter(Boolean)[0];
  const allowed = ["uz", "ru", "en"] as const;
  const isAllowed =
    firstSegment && (allowed as readonly string[]).includes(firstSegment);

  // Default language is uz-Latn (root path), not oz
  const currentLang = isAllowed ? firstSegment : "default";

  const bcp47Map: Record<string, string> = {
    default: "uz-Latn", // Root path (/) - Uzbek Latin (default) - matches your working site
    uz: "uz-Cyrl", // /uz path - Uzbek Cyrillic - matches your working site
    ru: "ru", // /ru path - Russian - matches your working site
    en: "en-US", // /en path - English - matches your working site
  };

  return (
    <I18nextProvider i18n={i18n}>
      <LangProvider lang={bcp47Map[currentLang] || "uz-Latn"}>
        <TooltipProvider>
          <RootDocument>
            <MiniAppAuthEffect />
            <Outlet />
          </RootDocument>
        </TooltipProvider>
      </LangProvider>
    </I18nextProvider>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  const lang = useLang();
  // Components key their content by "en-US", but <html lang> must match the
  // "en" hreflang code
  const htmlLang = lang === "en-US" ? "en" : lang;

  return (
    <html lang={htmlLang} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body className="flex flex-col h-dvh bg-gray-100">
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-WW54V5R"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {/* End Google Tag Manager (noscript) */}

        {children}
        <Toaster richColors position="top-center" />
        {/* <TanStackRouterDevtools position="bottom-right" />
        <ReactQueryDevtools buttonPosition="bottom-left" /> */}
        <Scripts />
      </body>
    </html>
  );
}
